import {
  ChatListItem,
  ChatType,
  ChatEntityReference,
  ChatUserReference,
} from '@choco-admin/types';

import type { ChatSearchChatFragmentFragment } from '../../../generated';

export type GeneratedChatFragment = ChatSearchChatFragmentFragment;

/**
 * Type guard to check if a string is a valid ChatType
 */
const isValidChatType = (type: string | null | undefined): type is ChatType => {
  return (
    type !== null &&
    type !== undefined &&
    ['ORDERING', 'SUPPORT'].includes(type)
  );
};

/**
 * Transform GraphQL user data to domain ChatUserReference
 */
const mapChatUsers = (
  users: ChatSearchChatFragmentFragment['users']
): {
  supplierUsers: readonly ChatUserReference[];
  buyerUsers: readonly ChatUserReference[];
} => {
  const supplierUsers: ChatUserReference[] = [];
  const buyerUsers: ChatUserReference[] = [];

  users.edges?.forEach(({ node }) => {
    const { id, phone, name, supplier, botUser } = node;
    const formattedUser: ChatUserReference = {
      id,
      phone: phone || '',
      name: name || '',
    };

    if (supplier) {
      if (!botUser) {
        supplierUsers.push(formattedUser);
      }
    } else {
      buyerUsers.push(formattedUser);
    }
  });

  return { supplierUsers, buyerUsers };
};

/**
 * Transform GraphQL entity to domain ChatEntityReference
 */
const mapChatEntity = <
  T extends {
    id: string;
    name: string | null;
    referenceId?: string | null | undefined;
  },
>(
  entity: T
): ChatEntityReference => ({
  id: entity.id,
  name: entity.name || '',
  referenceId: entity.referenceId || '',
});

/**
 * Maps a generated GraphQL chat fragment to the generic ChatListItem type from @choco-admin/types
 * This ensures consistency across all chat data transformations and avoids code duplication
 */
export const mapToChat = (
  chatFragment: GeneratedChatFragment
): ChatListItem => {
  const {
    id,
    restaurantName,
    chatName,
    buyer,
    supplier,
    deliveryAddress,
    users,
    type,
    enabled,
    customerNumber,
  } = chatFragment;

  const { supplierUsers, buyerUsers } = mapChatUsers(users);
  // The chat's supplier is exposed as a user; the entity lives on
  // linkedSupplier. Search hits sometimes omit the botUser flag, so the
  // entity is mapped whenever linkedSupplier is hydrated.
  const supplierEntity = supplier?.linkedSupplier ?? null;

  // Use deliveryAddress first, then buyer's fullAddress as fallback
  const fullAddress = deliveryAddress || buyer?.fullAddress || '';

  // Ensure enabled is properly converted to boolean
  const isEnabledValue = enabled === true;

  return {
    id,
    chatName: chatName || '',
    restaurantName: restaurantName || '',
    buyerUsers,
    supplierUsers,
    address: fullAddress,
    type: isValidChatType(type) ? type : 'ORDERING', // Default to ORDERING
    isEnabled: isEnabledValue,
    buyerEntity: buyer ? mapChatEntity(buyer) : null,
    supplierEntity: supplierEntity ? mapChatEntity(supplierEntity) : null,
    customerNumber: customerNumber ?? null,
  };
};
